/**
 * RequestAccessPage — iter150AC (sg_36203cc895e84a)
 *
 * Customer self-service: a signed-in user whose email isn't yet linked
 * to a Customer record asks the admin team to link them. Reuses the
 * /api/auth/request-customer-link endpoint, idempotent on retry.
 *
 * Surfaced from:
 *   - MyAccountPage error banner ("no company …")
 *   - Commissioning / Warranty 403 banners (`error_code: "not_linked"`)
 *   - Direct deep-link from emails / support replies.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldCheck, ArrowLeft, CheckCircle, Warning, Spinner,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useT } from "@/lib/i18n";

export default function RequestAccessPage({ user, onLogout }) {
  const navigate = useNavigate();
  const t = useT();
  const [email, setEmail] = useState(user?.email || "");
  const [companyName, setCompanyName] = useState("");
  const [intent, setIntent] = useState("commissioning");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);  // { ok, message } | { error }
  const validEmail = /^[^@]+@[^@]+\.[^@]+$/.test((email || "").trim());
  const validCompany = (companyName || "").trim().length >= 2;
  const canSubmit = validEmail && validCompany && !busy;

  async function submit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/auth/request-customer-link", {
        email: email.trim().toLowerCase(),
        company_name: companyName.trim(),
        intent,
        reason: (reason || "").trim() || undefined,
      });
      setResult({ ok: true, ...data });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      setResult({ error: typeof detail === "string" ? detail : t("request_access.err_generic", "Could not submit your request. Please try again.") });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="request-access-page">
      <header className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 md:px-6 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)}
                data-testid="request-access-back"
                className="w-8 h-8 inline-flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500">
          <ArrowLeft size={16} weight="bold" />
        </button>
        <ShieldCheck size={20} weight="fill" className="text-blue-700 shrink-0" />
        <div className="flex-1 min-w-0">
          <h1 className="text-base md:text-lg font-bold font-['Chivo'] text-slate-900 truncate">
            {t("request_access.title", "Request access to your company's account")}
          </h1>
        </div>
      </header>

      <main className="max-w-xl mx-auto px-4 md:px-6 py-6 space-y-4">
        <p className="text-sm text-slate-600 leading-relaxed">
          {t("request_access.subtitle",
             "Your email isn't linked to a customer record yet, so the portal " +
             "can't show your fleet, register a commissioning, or accept a " +
             "warranty claim. Tell us your company name and we'll link your " +
             "email so the next time you sign in everything just works.")}
        </p>

        {result?.ok ? (
          <div className="bg-emerald-50 border-2 border-emerald-200 rounded-sm p-4 space-y-2"
               data-testid="request-access-success">
            <div className="flex items-center gap-2">
              <CheckCircle size={20} weight="fill" className="text-emerald-700 shrink-0" />
              <h2 className="text-base font-bold text-emerald-900">
                {t("request_access.success_title", "Request received")}
              </h2>
            </div>
            <p className="text-sm text-emerald-900">{result.message}</p>
            <div className="text-[11px] text-emerald-800 font-mono pt-1 border-t border-emerald-200">
              ref: {result.id}
            </div>
            <button
              type="button"
              data-testid="request-access-back-home"
              onClick={() => navigate("/")}
              className="mt-2 text-xs font-bold uppercase tracking-wider px-3 h-8 rounded-sm bg-emerald-700 text-white hover:bg-emerald-800"
            >
              {t("request_access.back_home", "Back to home")}
            </button>
          </div>
        ) : (
          <form onSubmit={submit}
                className="bg-white border border-slate-200 rounded-sm p-4 md:p-5 space-y-3"
                data-testid="request-access-form">
            {result?.error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-xs p-2 rounded-sm flex items-start gap-2"
                   data-testid="request-access-error">
                <Warning size={14} weight="fill" className="shrink-0 mt-0.5" />
                <span>{result.error}</span>
              </div>
            )}

            <label className="block">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                {t("request_access.email_label", "Your email")}
              </span>
              <input type="email" value={email}
                     onChange={(e) => setEmail(e.target.value)}
                     data-testid="request-access-email"
                     autoComplete="email"
                     required
                     className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none" />
            </label>

            <label className="block">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                {t("request_access.company_label", "Company name")}
              </span>
              <input type="text" value={companyName}
                     onChange={(e) => setCompanyName(e.target.value)}
                     data-testid="request-access-company"
                     placeholder={t("request_access.company_placeholder", "e.g. Vinyl Extrusion")}
                     required
                     className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none" />
              <span className="block text-[10px] text-slate-500 mt-1">
                {t("request_access.company_hint", "Use the exact name on your invoices so we can match the right customer record.")}
              </span>
            </label>

            <label className="block">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                {t("request_access.intent_label", "What do you need access to?")}
              </span>
              <select value={intent}
                      onChange={(e) => setIntent(e.target.value)}
                      data-testid="request-access-intent"
                      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none bg-white">
                <option value="commissioning">{t("request_access.intent_commissioning", "Register commissioning of a new compressor")}</option>
                <option value="warranty_claim">{t("request_access.intent_warranty", "File a warranty claim")}</option>
                <option value="general">{t("request_access.intent_general", "Just see my fleet / quotes / files")}</option>
              </select>
            </label>

            <label className="block">
              <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                {t("request_access.reason_label", "Anything else we should know?")}
                <span className="font-normal normal-case text-slate-400 ml-1">{t("request_access.reason_optional", "(optional)")}</span>
              </span>
              <textarea value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        data-testid="request_access_reason"
                        rows={3} maxLength={1000}
                        placeholder={t("request_access.reason_placeholder", "Serial number you're trying to register, ACC rep you've spoken to, etc.")}
                        className="w-full px-3 py-2 text-sm border border-slate-300 rounded-sm focus:border-blue-500 focus:outline-none resize-none" />
            </label>

            <div className="flex items-center justify-between pt-1">
              <button type="button"
                      data-testid="request-access-cancel"
                      onClick={() => navigate(-1)}
                      className="text-xs font-bold uppercase tracking-wider px-3 h-9 rounded-sm border border-slate-300 text-slate-700 hover:bg-slate-50">
                {t("common.cancel", "Cancel")}
              </button>
              <button type="submit"
                      data-testid="request-access-submit"
                      disabled={!canSubmit}
                      className="text-xs font-bold uppercase tracking-wider px-4 h-9 rounded-sm bg-blue-700 text-white hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5">
                {busy
                  ? <><Spinner size={12} weight="bold" className="animate-spin" /> {t("request_access.submitting", "Sending…")}</>
                  : t("request_access.submit", "Send request")}
              </button>
            </div>
          </form>
        )}

        <p className="text-[11px] text-slate-500 leading-relaxed">
          {t("request_access.fine_print",
             "Submitting this form notifies the ACC admin team. We usually " +
             "respond within one business day. You'll receive an email once " +
             "your account is linked.")}
        </p>
      </main>
    </div>
  );
}
